(() => {
    class PredictiveSearch {
        constructor(inputElement, resultsContainer, options = {}) {
            this.input = inputElement;
            this.resultsContainer = resultsContainer;
            this.options = {
                debounceTime: options.debounceTime || 300,
                minQueryLength: options.minQueryLength || 2,
                sectionId: options.sectionId || 'predictive-search',
                onOpen: options.onOpen || null,
                onClose: options.onClose || null
            };

            this.currentQuery = '';
            this.abortController = null;
            this.debounceTimeout = null;
            this.originalContent = null;

            this.init();
        }

        init() {
            if (!this.input || !this.resultsContainer) {
                console.error('PredictiveSearch: input and resultsContainer are required');
                return;
            }

            this.originalContent = this.resultsContainer.innerHTML;
            this.clearButton = document.getElementById('search-modal-clear');
            this.updateClearButton();

            this.input.addEventListener('input', (e) => {
                this.handleInput(e);
                this.updateClearButton();
            });

            this.input.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    this.close();
                }
            });

            if (this.clearButton) {
                this.clearButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.clearSearch();
                });
            }

            const handleQueryClick = (e) => {
                const queryButton = e.target.closest('[data-query]');
                if (!queryButton) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                const query = queryButton.getAttribute('data-query');
                if (!query) {
                    return;
                }
                this.input.value = query;
                this.currentQuery = query.trim();
                this.updateClearButton();
                this.search(query.trim());
            };

            this.resultsContainer.addEventListener('click', handleQueryClick);

            const searchModal = this.resultsContainer.closest('.search-modal') ||
                this.resultsContainer.closest('[id*="search"]') ||
                document.querySelector('.search-modal');
            if (searchModal) {
                searchModal.addEventListener('click', handleQueryClick);
            }
        }

        handleInput(e) {
            const query = e.target.value.trim();
            this.currentQuery = query;

            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            if (query.length < this.options.minQueryLength) {
                if (this.originalContent) {
                    this.resultsContainer.innerHTML = this.originalContent;
                    const loader = this.resultsContainer.querySelector('#search-results-loader');
                    if (!loader) {
                        const loaderElement = document.createElement('div');
                        loaderElement.id = 'search-results-loader';
                        loaderElement.className = 'absolute inset-0 items-center justify-center bg-white z-10 hidden';

                        const spinnerContainer = document.createElement('div');
                        spinnerContainer.className = 'relative w-12 h-12';

                        const outerCircle = document.createElement('div');
                        outerCircle.className = 'absolute inset-0 border-4 border-gray-200 rounded-full';

                        const spinnerCircle = document.createElement('div');
                        spinnerCircle.className = 'absolute inset-0 border-4 border-transparent border-t-accent rounded-full animate-spin';

                        spinnerContainer.appendChild(outerCircle);
                        spinnerContainer.appendChild(spinnerCircle);
                        loaderElement.appendChild(spinnerContainer);
                        this.resultsContainer.appendChild(loaderElement);
                    }
                    this.open();

                    if (window.searchHistory && typeof window.searchHistory.showDefaultContent === 'function') {
                        window.searchHistory.showDefaultContent();
                    }
                } else {
                    this.close();
                }
                return;
            }

            this.debounceTimeout = setTimeout(() => {
                this.search(query);
            }, this.options.debounceTime);
        }

        search(query) {
            if (query !== this.currentQuery) {
                return;
            }

            if (this.abortController) {
                this.abortController.abort();
            }

            this.showLoader();

            this.abortController = new AbortController();

            const params = new URLSearchParams({
                q: query.trim(),
                section_id: this.options.sectionId,
                'resources[queries][limit]': '10'
            });

            const url = `/search/suggest?${params.toString()}`;

            fetch(url, {
                method: 'GET',
                signal: this.abortController.signal,
                headers: {
                    'Accept': 'text/html',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin',
                redirect: 'follow'
            })
                .then((response) => {
                    if (query !== this.currentQuery) {
                        return null;
                    }

                    if (!response.ok) {
                        return response.text().then(text => {
                            throw new Error(`HTTP ${response.status}: ${text.substring(0, 100)}`);
                        });
                    }
                    return response.text();
                })
                .then((html) => {
                    if (!html || query !== this.currentQuery) {
                        return;
                    }

                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');

                    let section = doc.querySelector(`#shopify-section-${this.options.sectionId}`);
                    if (!section) {
                        section = doc.querySelector('#predictive-search-results');
                    }
                    if (!section && doc.body) {
                        section = doc.body.querySelector(`#shopify-section-${this.options.sectionId}`) ||
                            doc.body.querySelector('#predictive-search-results');
                    }

                    let content = '';
                    if (section?.innerHTML.trim()) {
                        content = section.innerHTML.trim();
                    } else if (doc.body) {
                        const bodyContent = doc.body.innerHTML.trim();
                        if (bodyContent) {
                            const tempDiv = doc.createElement('div');
                            tempDiv.innerHTML = bodyContent;
                            const resultsDiv = tempDiv.querySelector('#predictive-search-results');
                            content = resultsDiv ? resultsDiv.innerHTML.trim() : bodyContent;
                        }
                    }

                    const loader = this.resultsContainer.querySelector('#search-results-loader');

                    if (content) {
                        this.resultsContainer.innerHTML = content;
                    } else {
                        const emptyState = document.createElement('div');
                        emptyState.className = 'predictive-search-empty px-4 py-6 text-center text-gray-600 text-sm min-h-[400px] flex items-center justify-center';
                        const emptyText = document.createElement('p');
                        emptyText.textContent = 'No results found';
                        emptyState.appendChild(emptyText);
                        this.resultsContainer.innerHTML = '';
                        this.resultsContainer.appendChild(emptyState);
                    }

                    if (loader && !this.resultsContainer.querySelector('#search-results-loader')) {
                        this.resultsContainer.appendChild(loader);
                    }

                    this.hideLoader();

                    this.open();

                    const resultsContent = Array.from(this.resultsContainer.children).filter(
                        child => child.id !== 'search-results-loader'
                    );
                    resultsContent.forEach(child => {
                        child.classList.remove('hidden');
                    });

                    if (typeof window.initializeProductCardVariants === 'function') {
                        window.initializeProductCardVariants(this.resultsContainer);
                    }

                    const resultsDiv = this.resultsContainer.querySelector('#predictive-search-results');
                    const hasNoResults = this.resultsContainer.querySelector('.predictive-search-empty') !== null;

                    const hasActualResults = resultsDiv !== null && !hasNoResults;

                    if (hasActualResults && query && query.trim().length >= this.options.minQueryLength && (window.searchHistory && typeof window.searchHistory.trackSearch === 'function')) {
                        window.searchHistory.trackSearch(query.trim());
                        if (typeof window.searchRecentSearches?.renderRecentSearches === 'function') {
                            window.searchRecentSearches.renderRecentSearches();
                        }
                    }

                    const recentSearchesSection = document.getElementById('recent-searches-section');
                    if (recentSearchesSection) recentSearchesSection.classList.add('hidden');
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }
                    console.error('Predictive search error:', error);
                    console.error('Failed URL:', url);

                    const loader = this.resultsContainer.querySelector('#search-results-loader');

                    const errorState = document.createElement('div');
                    errorState.className = 'predictive-search-empty px-4 py-6 text-center text-gray-600 text-sm min-h-[400px] flex items-center justify-center';
                    const errorText = document.createElement('p');
                    errorText.textContent = 'Search unavailable. Please try again.';
                    errorState.appendChild(errorText);
                    this.resultsContainer.innerHTML = '';
                    this.resultsContainer.appendChild(errorState);

                    if (loader && !this.resultsContainer.querySelector('#search-results-loader')) {
                        this.resultsContainer.appendChild(loader);
                    }

                    this.hideLoader();
                    this.open();

                    if (typeof window.initializeProductCardVariants === 'function') {
                        window.initializeProductCardVariants(this.resultsContainer);
                    }
                });
        }

        showLoader() {
            if (!this.resultsContainer) return;

            if (!this.originalContent) {
                this.originalContent = this.resultsContainer.innerHTML;
            }

            this.resultsContainer.classList.add('relative');

            const loader = this.resultsContainer.querySelector('#search-results-loader');

            const resultsContent = Array.from(this.resultsContainer.children).filter(
                child => child.id !== 'search-results-loader'
            );
            resultsContent.forEach(child => {
                child.classList.add('hidden');
            });

            if (loader) {
                loader.classList.remove('hidden');
                loader.classList.add('flex');
            }

            this.resultsContainer.classList.remove('hidden', 'invisible', 'opacity-0');
            this.resultsContainer.classList.add('block', 'visible', 'opacity-100');
            this.resultsContainer.setAttribute('aria-hidden', 'false');
        }

        hideLoader() {
            if (!this.resultsContainer) return;

            const loader = this.resultsContainer.querySelector('#search-results-loader');
            if (loader) {
                loader.classList.add('hidden');
                loader.classList.remove('flex');
            }

            const resultsContent = Array.from(this.resultsContainer.children).filter(
                child => child.id !== 'search-results-loader'
            );
            resultsContent.forEach(child => {
                child.classList.remove('hidden');
            });
        }

        open() {
            if (!this.resultsContainer) {
                return;
            }
            this.resultsContainer.classList.remove('hidden', 'invisible', 'opacity-0');
            this.resultsContainer.classList.add('block', 'visible', 'opacity-100');
            this.resultsContainer.setAttribute('aria-hidden', 'false');
            if (this.options.onOpen) {
                this.options.onOpen();
            }
        }

        close() {
            if (!this.resultsContainer) {
                return;
            }
            this.resultsContainer.classList.remove('block', 'visible', 'opacity-100');
            this.resultsContainer.classList.add('hidden', 'invisible', 'opacity-0');
            this.resultsContainer.setAttribute('aria-hidden', 'true');
            if (this.options.onClose) {
                this.options.onClose();
            }
        }

        updateClearButton() {
            if (!this.clearButton) return;
            const hasValue = this.input.value.trim().length > 0;
            if (hasValue) {
                this.clearButton.classList.remove('hidden');
            } else {
                this.clearButton.classList.add('hidden');
            }
        }

        clearSearch() {
            this.input.value = '';
            this.currentQuery = '';
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }
            if (this.abortController) {
                this.abortController.abort();
            }
            this.updateClearButton();
            this.hideLoader();
            if (this.originalContent) {
                this.resultsContainer.innerHTML = this.originalContent;
                if (window.searchHistory && typeof window.searchHistory.showDefaultContent === 'function') {
                    window.searchHistory.showDefaultContent();
                }
            } else {
                this.close();
            }
        }

        destroy() {
            if (this.abortController) {
                this.abortController.abort();
            }
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }
        }
    }

    window.PredictiveSearch = PredictiveSearch;
})();

